import { computed, reactive, ref, watch, type Ref } from 'vue';
import { format } from 'date-fns';
import { drugUnitValue } from '@/composables/inventory/inventoryTable';
import { useInventoryScope } from '@/composables/inventory/useInventoryScope';
import { useUnsavedInputGuard } from '@/composables/inventory/useUnsavedInputGuard';
import { store } from '@/plugins/store';
import { BulkSaveRejectedError } from '@/plugins/store/actions/queries/inventory';

type BulkRow = { drug_id: number; unit_id: number };

/** One row of the dialog's table, as the query hands it over. */
type RowOf<Data extends { rows: BulkRow[] }> = Data['rows'][number];

export type BulkEntryDialogOptions<Data extends { rows: BulkRow[] }, Entry extends object, Payload> = {
    isOpen: Ref<boolean>;
    /** The query of the rows one farm offers on one day. */
    useRows: (farmId: Ref<number | null>, date: Ref<string>, options: { enabled: Ref<boolean> }) => { data: Ref<Data | undefined>; isFetching: Ref<boolean>; isError: Ref<boolean>; refetch: () => unknown };
    /** The mutation that saves the dialog. */
    useSave: () => { mutateAsync: (payload: Payload) => Promise<{ saved: number }>; isPending: Ref<boolean> };
    /** The entry a row starts from. */
    entryFor: (row: RowOf<Data>) => Entry;
    /** Whether an entry holds input of the reader's own, which a change of farm or closing the dialog would lose. */
    isEntryDirty: (entry: Entry) => boolean;
};

function clearRecord(record: Record<string, unknown>) {
    Object.keys(record).forEach((key) => delete record[key]);
}

/**
 * The shell of a farm-wide bulk dialog (the count and the purchase): the farm and day, an entry per row keyed by
 * drug and unit, the guard against losing what was typed, and the save with the server's refusals mapped back to
 * their rows. Each dialog brings its rows, its entries and the payload it saves.
 */
export function useBulkEntryDialog<Data extends { rows: BulkRow[] }, Entry extends object, Payload>({ isOpen, useRows, useSave, entryFor, isEntryDirty }: BulkEntryDialogOptions<Data, Entry, Payload>) {
    /** Bulk entries are written to a farm, so only the farms the reader has ReadWrite on can be picked. */
    const { writableFarms } = useInventoryScope();

    const farmId = ref<number | null>(null);
    /** Today as of the last opening, not of the mount: the dialogs stay mounted with the page, which can stay open past midnight. */
    const todayIso = ref(format(new Date(), 'yyyy-MM-dd'));
    const dateValue = ref(todayIso.value);
    const entries = reactive<Record<string, Entry>>({}) as Record<string, Entry>;
    /** Rows the server refused on the last save, by row key. */
    const serverErrors = reactive<Record<string, string>>({});
    /** Messages the server gave about the farm or the date, which belong to no row. */
    const generalErrors = ref<string[]>([]);

    /** The picker hands back a full ISO timestamp once a day is chosen; the endpoint wants the day alone. */
    const dateOnly = computed(() => {
        if (!dateValue.value) {
            return '';
        }

        return dateValue.value.length === 10 ? dateValue.value : format(new Date(dateValue.value), 'yyyy-MM-dd');
    });

    const { data, isFetching, isError, refetch } = useRows(farmId, dateOnly, { enabled: computed(() => isOpen.value && farmId.value !== null && dateValue.value !== '') });
    const { mutateAsync, isPending: isSaving } = useSave();

    const rows = computed<RowOf<Data>[]>(() => data.value?.rows ?? []);

    /**
     * Gives every row without input of the reader's own a fresh entry, so the inputs always have something to bind
     * to and an untouched row starts from what the loaded day offers. What was typed for a row is kept.
     */
    function seedEntries(loaded: RowOf<Data>[]) {
        loaded.forEach((row) => {
            const key = drugUnitValue(row);
            const entry = entries[key];

            if (!entry || !isEntryDirty(entry)) {
                entries[key] = entryFor(row);
            }
        });
    }

    // A row gets its entry as soon as it is loaded. What was typed for a row survives a change of date.
    watch(rows, seedEntries, { immediate: true, flush: 'sync' });

    // Judged over the entries themselves, so input survives while the rows are away (a refetch in flight or failed).
    const isDirty = computed(() => Object.values(entries).some(isEntryDirty));
    const { pendingAction, currentFarmName, requestFarm, requestOpen, confirm, keep } = useUnsavedInputGuard(farmId, isOpen, isDirty);

    // Every opening starts from a blank dialog at the first writable farm in the dashboard's selection.
    watch(isOpen, (open) => {
        if (!open) {
            return;
        }

        const selected = writableFarms.value.find((farm) => store.state.selectedFarmIds.includes(farm.farm_id));

        farmId.value = (selected ?? writableFarms.value[0])?.farm_id ?? null;
        todayIso.value = format(new Date(), 'yyyy-MM-dd');
        dateValue.value = todayIso.value;
        pendingAction.value = null;
        clearRecord(entries);
        // Reopened on the same farm and day, the rows are the ones already loaded and the watch above does not fire again.
        seedEntries(rows.value);
        clearRecord(serverErrors);
        generalErrors.value = [];
    });

    // Another farm means another set of drugs; what was typed belongs to the farm it was typed for.
    watch(farmId, () => {
        clearRecord(entries);
        clearRecord(serverErrors);
        generalErrors.value = [];
    });

    /**
     * Saves the payload and closes the dialog, resolving with the number of rows saved, or null when the save failed.
     * Rows the server refuses land in `serverErrors`, its other messages in `generalErrors`.
     */
    async function submit(payload: Payload): Promise<number | null> {
        clearRecord(serverErrors);
        generalErrors.value = [];

        try {
            const { saved } = await mutateAsync(payload);

            isOpen.value = false;

            return saved;
        } catch (error) {
            if (error instanceof BulkSaveRejectedError) {
                error.rowErrors.forEach((rowError) => {
                    if (rowError.drug_id === null || rowError.dosage_unit_id === null) {
                        generalErrors.value.push(rowError.message);
                    } else {
                        serverErrors[drugUnitValue({ drug_id: rowError.drug_id, unit_id: rowError.dosage_unit_id })] = rowError.message;
                    }
                });
            }

            return null;
        }
    }

    return {
        writableFarms,
        farmId,
        dateValue,
        dateOnly,
        todayIso,
        entries,
        serverErrors,
        generalErrors,
        data,
        rows,
        isFetching,
        isError,
        refetch,
        isSaving,
        submit,
        pendingAction,
        currentFarmName,
        requestFarm,
        requestOpen,
        confirm,
        keep,
    };
}
